import { useState } from "react";
import { useGeocode, type PlannerContext, type Point } from "../../api/routing";

export type Picking = "origin" | "meeting" | null;

/** Step 2, where the day starts: the depot every day leaves from, and an
 * optional meeting point the crew calls at first. Meeting points saved for the
 * chosen contract are offered by name. */
export default function StartStep({
  context,
  contractId,
  origin,
  meeting,
  onOrigin,
  onMeeting,
  picking,
  onPicking,
}: {
  context: PlannerContext;
  contractId: string;
  origin: Point | null;
  meeting: Point | null;
  onOrigin: (p: Point | null) => void;
  onMeeting: (p: Point | null) => void;
  picking: Picking;
  onPicking: (p: Picking) => void;
}) {
  return (
    <>
      <PointPicker
        label="Depot"
        point={origin}
        onChange={onOrigin}
        picking={picking === "origin"}
        onPick={() => onPicking(picking === "origin" ? null : "origin")}
        saved={[]}
      />
      <PointPicker
        label="Meeting point (optional)"
        point={meeting}
        onChange={onMeeting}
        picking={picking === "meeting"}
        onPick={() => onPicking(picking === "meeting" ? null : "meeting")}
        saved={context.meeting_points.filter((m) => contractId === "" || String(m.contract_id) === contractId)}
        clearable
      />
    </>
  );
}

/** An address, a "lat, lon", a saved point, or a click on the map. The point
 * only changes once the geocoder has found it, so a typo never moves the
 * depot to nowhere. */
function PointPicker({
  label,
  point,
  onChange,
  picking,
  onPick,
  saved,
  clearable = false,
}: {
  label: string;
  point: Point | null;
  onChange: (p: Point | null) => void;
  picking: boolean;
  onPick: () => void;
  saved: PlannerContext["meeting_points"];
  clearable?: boolean;
}) {
  const geocode = useGeocode();
  const [text, setText] = useState("");
  function find() {
    const wanted = text.trim();
    geocode.mutate(wanted, {
      onSuccess: (r) => {
        if (r.lat !== null && r.lon !== null) {
          onChange({ lat: r.lat, lon: r.lon, label: wanted });
        }
      },
    });
  }
  const notFound = geocode.isSuccess && geocode.data.lat === null;
  return (
    <div className="point-picker">
      <label className="field">
        {label}
        <input
          className="input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && text.trim() !== "") {
              e.preventDefault();
              find();
            }
          }}
          placeholder="Address, place, or lat, lon"
        />
      </label>
      <div className="point-picker-row">
        <button type="button" className="row-button" disabled={geocode.isPending || text.trim() === ""} onClick={find}>
          {geocode.isPending ? "Finding…" : "Find"}
        </button>
        <button type="button" className="row-button" aria-pressed={picking} onClick={onPick}>
          {picking ? "Click the map…" : "Pick on map"}
        </button>
        {clearable && point ? (
          <button type="button" className="row-button" onClick={() => onChange(null)}>
            Clear
          </button>
        ) : null}
      </div>
      {saved.length > 0 ? (
        <select
          className="select"
          aria-label={`Saved ${label.toLowerCase()}`}
          value=""
          onChange={(e) => {
            const found = saved.find((m) => String(m.id) === e.target.value);
            if (found) {
              onChange({ lat: found.lat, lon: found.lon, label: found.name });
            }
          }}
        >
          <option value="">Saved points…</option>
          {saved.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      ) : null}
      <span className="point-label">
        {point ? `${point.label} (${point.lat.toFixed(5)}, ${point.lon.toFixed(5)})` : notFound ? "Not found. Try a fuller address, or pick it on the map." : "Not set"}
      </span>
      {geocode.isError ? <span className="notice bad">{geocode.error.message}</span> : null}
    </div>
  );
}
